/*
 * @Author: mina.min
 * @Date: 2025-06-09 16:23:39
 * @LastEditors: mina.min
 * @LastEditTime: 2025-07-08 11:57:45
 * @FilePath: /OTAManagementSystem/src/pages/ModuleManagement/ModuleList.tsx
 * @Description: 模块列表组件
 */
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import {
  Button,
  Card,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Typography,
} from 'antd'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import type { ModuleInfo } from '../../store/moduleStore'
import { moduleStore } from '../../store/moduleStore'
import { formatToCST } from '../../utils'
import './ModuleList.scss'

const { Option } = Select
const { Title } = Typography

// 进制转换工具函数
const convertToHex = (decimal: number): string => {
  if (decimal === 0) return '0x00'
  const hex = decimal.toString(16).toUpperCase()
  // 确保至少显示2位16进制数字
  return `0x${hex.padStart(2, '0')}`
}

const convertToDecimal = (hex: string): number => {
  // 去掉0x前缀并转换为10进制
  const cleanHex = hex.replace(/^0x/i, '')
  return parseInt(cleanHex, 16)
}

const validateHex = (hex: string): boolean => {
  // 验证16进制格式
  const pattern = /^0x[0-9A-Fa-f]+$/
  return pattern.test(hex)
}

const ModuleList: React.FC = observer(() => {
  const [form] = Form.useForm()
  const [modalVisible, setModalVisible] = useState(false)
  const [editingModule, setEditingModule] = useState<ModuleInfo | null>(null)

  useEffect(() => {
    moduleStore.getModuleList()
  }, [])

  const handleTableChange = (pagination: any) => {
    moduleStore.getModuleList(pagination.current, pagination.pageSize)
  }

  const handleAdd = () => {
    setEditingModule(null)
    form.resetFields()
    // 设置默认值
    form.setFieldsValue({
      hardwareId: '0x',
    })
    setModalVisible(true)
  }

  const handleEdit = async (record: ModuleInfo) => {
    try {
      // 从后端获取最新的模块详情数据
      const detail = await moduleStore.getModuleDetail(record.id)
      if (detail) {
        setEditingModule(detail)
        // 将10进制转换为16进制显示
        const formValues = {
          ...detail,
          hardwareId:
            detail.hardwareId !== undefined && detail.hardwareId !== null
              ? convertToHex(detail.hardwareId)
              : '',
        }
        form.setFieldsValue(formValues)
        setModalVisible(true)
      } else {
        message.error('获取模块详情失败')
      }
    } catch (error: any) {
      message.error(error?.info || error?.message || '获取模块详情失败')
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await moduleStore.deleteModule(id)
      message.success('删除成功')
    } catch (error: any) {
      message.error(error?.info || error?.message || '操作失败')
    }
  }

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields()

      // 将16进制转换为10进制
      const submitValues = {
        ...values,
        hardwareId: convertToDecimal(values.hardwareId),
      }

      if (editingModule) {
        await moduleStore.updateModule(editingModule.id, submitValues)
        message.success('更新成功')
      } else {
        await moduleStore.addModule(submitValues)
        message.success('添加成功')
      }
      setModalVisible(false)
    } catch (error: any) {
      message.error(error?.info || error?.message || '操作失败')
    }
  }

  const columns = [
    {
      title: '序号',
      key: 'index',
      width: 80,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: '模块名称',
      dataIndex: 'moduleName',
      key: 'moduleName',
      sorter: (a: ModuleInfo, b: ModuleInfo) => {
        const nameA = a.moduleName.toLowerCase()
        const nameB = b.moduleName.toLowerCase()
        return nameA.localeCompare(nameB)
      },
      defaultSortOrder: 'ascend' as const,
    },
    {
      title: '描述',
      dataIndex: 'remark',
      key: 'remark',
    },
    {
      title: '硬件ID',
      dataIndex: 'hardwareId',
      key: 'hardwareId',
      render: (hardwareId: number) =>
        hardwareId !== undefined && hardwareId !== null ? convertToHex(hardwareId) : '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <span style={{ color: status === 'START' ? '#52c41a' : '#ff4d4f' }}>
          {status === 'START' ? '启用' : '停用'}
        </span>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdTime',
      key: 'createdTime',
      render: (createdTime: string) => formatToCST(createdTime),
    },
    {
      title: '最后更新时间',
      dataIndex: 'lastUpdated',
      key: 'lastUpdated',
      render: (lastUpdated: string) => formatToCST(lastUpdated),
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right' as const,
      width: 160,
      render: (_: any, record: ModuleInfo) => (
        <Space size='middle'>
          <Button type='link' icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title={`确定删除模块"${record.moduleName}"吗？`}
            description='删除后该模块将从系统中移除，此操作不可恢复。'
            onConfirm={() => handleDelete(record.id)}
            okText='确定'
            cancelText='取消'
            okType='danger'
          >
            <Button type='link' danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div className='module-list'>
      <div className='module-list-header'>
        <Title level={4}>模块管理</Title>
        <Button type='primary' icon={<PlusOutlined />} onClick={handleAdd}>
          添加模块
        </Button>
      </div>

      <Card className='module-list-card'>
        <Table
          columns={columns}
          dataSource={moduleStore.moduleList}
          rowKey='id'
          loading={moduleStore.loading}
          scroll={{ x: 1200 }}
          pagination={moduleStore.pagination}
          onChange={handleTableChange}
        />
      </Card>

      <Modal
        title={editingModule ? '编辑模块' : '添加模块'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        destroyOnHidden
      >
        <Form form={form} layout='vertical' initialValues={{ status: 'START' }}>
          <Form.Item
            name='moduleName'
            label='模块名称'
            rules={[{ required: true, message: '请输入模块名称' }]}
          >
            <Input placeholder='请输入模块名称' />
          </Form.Item>

          <Form.Item name='remark' label='描述' rules={[{ required: true, message: '请输入描述' }]}>
            <Input.TextArea placeholder='请输入描述' />
          </Form.Item>
          <Form.Item
            name='hardwareId'
            label='硬件ID (16进制)'
            rules={[
              {
                validator: (_, value) => {
                  if (!value || value === '0x') {
                    return Promise.reject(new Error('请输入硬件ID'))
                  }
                  if (!validateHex(value)) {
                    return Promise.reject(new Error('请输入有效的16进制格式，如: 0x1234'))
                  }
                  const decimal = convertToDecimal(value)
                  if (decimal > 2147483647) {
                    return Promise.reject(new Error('输入值已超出最大限制'))
                  }
                  return Promise.resolve()
                },
              },
            ]}
          >
            <Input placeholder='请在0x后输入16进制数字' />
          </Form.Item>
          <Form.Item name='status' label='状态' rules={[{ required: true, message: '请选择状态' }]}>
            <Select placeholder='请选择状态'>
              <Option value='START'>启动</Option>
              <Option value='DISABLED'>禁用</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
})

export default ModuleList
